import { useEffect, useId, useMemo, useState } from 'react'
import type { MemberListItem } from '@ai-meeting/shared'
import { fmt } from '@/constants/ui-locale'
import { useInviteMeetingMembers } from '@/hooks/useInviteMeetingMembers'
import { useUiStrings } from '@/hooks/useUiStrings'
import ShareWithMembersField from '@/pages/Home/components/ShareWithMembersField'
import { normalizeMemberName } from '@/pages/Meetings/utils/normalizeMemberName'

export interface InviteMembersModalProps {
  meetingId: string
  meetingTitle: string
  open: boolean
  onClose: () => void
  /** Fired after a successful invite with the recipient count (for toast). */
  onInvited?: (count: number) => void
  /** Display names already in the shared room — disabled as "In this room". */
  inRoomMemberNames?: readonly string[]
}

/**
 * Mid-call invite dialog: reuses the Create Meeting member picker and the
 * same `createMeetingShares` path so live invites behave like create-time ones.
 *
 * Success closes immediately and reports `onInvited` for the host toast;
 * failure keeps the selection for Retry.
 */
export default function InviteMembersModal({
  meetingId,
  meetingTitle,
  open,
  onClose,
  onInvited,
  inRoomMemberNames = [],
}: InviteMembersModalProps) {
  const { t } = useUiStrings()
  const titleId = useId()
  const { invite, submitting, error, clearError, setError } =
    useInviteMeetingMembers(meetingId)

  const [selected, setSelected] = useState<MemberListItem[]>([])

  const inRoomNames = useMemo(() => {
    const names = new Set<string>()
    for (const name of inRoomMemberNames) {
      names.add(normalizeMemberName(name))
    }
    return names
  }, [inRoomMemberNames])

  useEffect(() => {
    if (!open) return
    setSelected([])
    clearError()
  }, [open, clearError])

  if (!open) return null

  async function handleSubmit() {
    const result = await invite(selected)
    if (!result.ok) return
    const count = result.count
    setSelected([])
    onClose()
    onInvited?.(count)
  }

  function handleSelectedChange(next: MemberListItem[]) {
    setSelected(next)
    if (error) clearError()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="invite-members-modal"
    >
      <button
        type="button"
        aria-label={t.liveInviteClose}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
        disabled={submitting}
      />

      <div className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-[var(--text-primary)]">
              {t.liveInviteTitle}
            </h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {fmt(t.liveInviteHint, { meeting: meetingTitle })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label={t.liveInviteClose}
            className="shrink-0 rounded-lg p-1 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            <span aria-hidden className="block text-lg leading-none">
              ×
            </span>
          </button>
        </div>

        <div className="mt-4">
          <ShareWithMembersField
            selected={selected}
            onChange={handleSelectedChange}
            disabled={submitting}
            inRoomNames={inRoomNames}
            inRoomLabel={t.liveInviteInThisRoom}
          />
        </div>

        {error ? (
          <p className="mt-3 text-sm text-[var(--error)]" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSubmit().catch((err: unknown) => {
                setError(
                  err instanceof Error ? err.message : t.failedShareMeetingService
                )
              })
            }}
            disabled={submitting || selected.length === 0}
            className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? t.liveInviteSending : t.liveInviteSubmit}
          </button>
        </div>
      </div>
    </div>
  )
}
